interface NewInvestmentsConfig {
  newInvestments?: string[]
  style?: "unordered" | "ordered"
}

export class NewInvestmentsTool {
  private api: any
  private config: NewInvestmentsConfig
  private block: any
  private isProcessing: boolean = false
  private headerInserted: boolean = false

  static get toolbox() {
    return {
      title: "New investments",
      icon: '<svg width="20" height="20" viewBox="0 0 20 20"><path d="M10 2l2.1 4.26L17 7l-3.4 3.32L14.2 15 10 12.9 5.8 15l.6-4.68L3 7l4.9-.74L10 2z" fill="currentColor"/></svg>',
    }
  }

  constructor({
    api,
    config,
    block,
    data,
  }: {
    api: any
    config: NewInvestmentsConfig
    block: any
    data?: { header_inserted?: boolean }
  }) {
    this.api = api
    this.config = config || {}
    this.block = block
    this.headerInserted = data?.header_inserted || false
  }

  render() {
    if (this.api.readOnly?.isEnabled) {
      const investments = (this.config.newInvestments || []).filter(Boolean)
      if (investments.length === 0) {
        const p = document.createElement("p")
        p.textContent = "No new investments this quarter."
        return p
      }
      const ul = document.createElement("ul")
      ul.className = "ej-new-investments-content"
      investments.forEach((text) => {
        const li = document.createElement("li")
        li.textContent = text
        ul.appendChild(li)
      })
      return ul
    }

    const root = document.createElement("div")
    root.className = "ej-new-investments-inserter"

    // Check if we need to insert a header above this block
    queueMicrotask(() => {
      this.checkAndInsertHeaderIfNeeded()
    })

    setTimeout(() => {
      if (!this.isProcessing) {
        this.insertListAndReplace()
      }
    }, 0)

    return root
  }

  private async checkAndInsertHeaderIfNeeded() {
    try {
      // Short-circuit if header has already been inserted
      if (this.headerInserted) {
        return
      }

      // Find this block's index by searching through all blocks
      let currentBlockIndex = -1
      const totalBlocks = this.api.blocks.getBlocksCount()

      for (let i = 0; i < totalBlocks; i++) {
        const block = this.api.blocks.getBlockByIndex(i)
        if (block && block.id === this.block.id) {
          currentBlockIndex = i
          break
        }
      }

      if (currentBlockIndex === -1) {
        console.error(
          "ERROR: Could not find current block, skipping header insertion",
        )
        return
      }

      if (currentBlockIndex > 0) {
        const previousBlock = this.api.blocks.getBlockByIndex(
          currentBlockIndex - 1,
        )

        if (previousBlock) {
          // Await the saved data from the previous block
          const previousBlockData = await previousBlock.save()

          // Check if it's a header block with our specific text and level
          if (
            previousBlockData.tool === "header" &&
            previousBlockData.data.text === "New Investments" &&
            previousBlockData.data.level === 3
          ) {
            // Header exists - mark as inserted even though we didn't insert it
            this.headerInserted = true
            return
          }
        }
      }

      this.insertHeaderBlock(currentBlockIndex)
    } catch (error) {
      console.error("Error in checkAndInsertHeaderIfNeeded:", error)
    }
  }

  private insertHeaderBlock(currentBlockIndex: number) {
    try {
      // Insert a header block above the current block
      this.api.blocks.insert(
        "header",
        {
          text: "New Investments",
          level: 3,
        },
        {},
        currentBlockIndex,
        true,
      )

      this.headerInserted = true

      // Move focus back to the content block after insertion
      setTimeout(() => {
        this.api.caret.setToBlock(currentBlockIndex + 1)
      }, 50)
    } catch (error) {
      console.error("Error inserting header block:", error)
    }
  }

  private async insertListAndReplace() {
    if (this.isProcessing) return
    this.isProcessing = true

    try {
      const newInvestments = (this.config.newInvestments || []).filter(Boolean)

      // Find our current position
      const currentIndex = this.findCurrentBlockIndex()
      if (currentIndex === -1) {
        console.warn("Could not find current block index")
        return
      }

      // Prepare list data
      let listData
      if (newInvestments.length === 0) {
        // Insert a paragraph instead
        await this.api.blocks.insert(
          "paragraph",
          { text: "No new investments this quarter." },
          {},
          currentIndex,
          false,
        )
      } else {
        listData = {
          style: this.config.style || "unordered",
          items: newInvestments.map((text) => ({
            content: text,
            items: [], // Empty array for nested items
          })),
        }

        // Insert the list block
        await this.api.blocks.insert("list", listData, {}, currentIndex, false)
      }

      // Small delay to ensure the block is fully inserted.
      // Re-find the inserter block by ID because concurrent tool insertions
      // on the same page shift indices between insertion and cleanup.
      setTimeout(() => {
        try {
          const inserterIndex = this.findCurrentBlockIndex()
          if (inserterIndex !== -1) {
            this.api.blocks.delete(inserterIndex)
            this.api.caret.setToBlock(inserterIndex - 1)
          }
        } catch (error) {
          console.error("Error cleaning up inserter block:", error)
        }
      }, 50)
    } catch (error) {
      console.error("Error inserting new investments list:", error)
      this.isProcessing = false
    }
  }

  private findCurrentBlockIndex(): number {
    const totalBlocks = this.api.blocks.getBlocksCount()

    for (let i = 0; i < totalBlocks; i++) {
      try {
        const block = this.api.blocks.getBlockByIndex(i)
        if (block && block.id === this.block.id) {
          return i
        }
      } catch (error) {
        continue
      }
    }

    return -1
  }

  save() {
    return {
      header_inserted: this.headerInserted,
    }
  }

  static get isReadOnlySupported() {
    return true
  }
}

// Factory function to create the tool with specific config
export const createNewInvestmentsTool = (config: NewInvestmentsConfig) => {
  return class extends NewInvestmentsTool {
    constructor(toolConfig: any) {
      super({
        ...toolConfig,
        config: { ...config, ...toolConfig.config },
      })
    }
  }
}
